import React from 'react';
import { BanknoteIcon, ShieldCheckIcon } from 'lucide-react';
import { Draft } from '../../types/booking';
import { euro } from '../../utils/format';
type Props = {
  draft: Draft;
  errors: Record<string, string>;
  deposit: number;
  amount: number;
  onChange: (patch: Partial<Draft>) => void;
};
export function StepDetails({
  draft,
  errors,
  deposit,
  amount,
  onChange
}: Props) {
  const field = (invalid: boolean) => `mt-2 w-full rounded-xl border bg-carbon px-4 py-3.5 text-sm text-chalk transition-colors duration-200 ease-smooth placeholder:text-dim/60 focus:border-ember ${invalid ? 'border-red-400' : 'border-edge'}`;
  const set = (patch: Partial<Draft['customer']>) => onChange({
    customer: {
      ...draft.customer,
      ...patch
    }
  });
  const methods = [{
    id: 'deposit' as const,
    icon: ShieldCheckIcon,
    title: `Aanbetaling ${euro(deposit)}`,
    text: `Direct online via iDEAL. Uw slot staat meteen vast, het restant (${euro(amount - deposit)}) betaalt u na de behandeling.`,
    badge: 'Direct bevestigd'
  }, {
    id: 'studio' as const,
    icon: BanknoteIcon,
    title: 'Betalen in de werkplaats',
    text: 'Wij nemen uw aanvraag in behandeling en bellen u ter bevestiging. Betalen doet u na afronding.',
    badge: 'Bevestiging binnen 24 uur'
  }];
  return <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12" data-id="element-413" data-mp="1ju0j7xt5lt58">
      <div data-id="element-414" data-mp="27itl0awgiv7r">
        <h3 className="text-xs uppercase tracking-[0.2em] text-dim" data-id="element-415" data-mp="27kef5zev0c53">Uw gegevens</h3>
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-416" data-mp="2cifzeno0uhi5">
          <div data-id="element-417" data-mp="1xkxa9k7fm2a6">
            <label htmlFor="name" className="text-sm font-medium text-chalk" data-id="element-418" data-mp="1xlf5cdicyagi">
              Naam
            </label>
            <input id="name" type="text" autoComplete="name" value={draft.customer.name} onChange={e => set({
            name: e.target.value
          })} aria-invalid={Boolean(errors.name)} className={field(Boolean(errors.name))} placeholder="Uw naam" data-id="element-419" data-mp="22iyu47com5mp" />
            {errors.name && <p className="mt-1.5 text-xs text-red-400" data-id="element-420" data-mp="2orvsq3g3hcj4">{errors.name}</p>}
          </div>
          <div data-id="element-421" data-mp="2k1p3q2ho7q0c">
            <label htmlFor="phone" className="text-sm font-medium text-chalk" data-id="element-422" data-mp="2k15leko3yzls">
              Telefoonnummer
            </label>
            <input id="phone" type="tel" autoComplete="tel" value={draft.customer.phone} onChange={e => set({
            phone: e.target.value
          })} aria-invalid={Boolean(errors.phone)} className={field(Boolean(errors.phone))} placeholder="06 12 34 56 78" data-id="element-423" data-mp="2f1t38d86mgpj" />
            {errors.phone && <p className="mt-1.5 text-xs text-red-400" data-id="element-424" data-mp="1w4ruchbibwgb">{errors.phone}</p>}
          </div>
        </div>

        <div className="mt-5" data-id="element-425" data-mp="3vfsw548m163a">
          <label htmlFor="email" className="text-sm font-medium text-chalk" data-id="element-426" data-mp="3vern15b5mto7">
            E-mailadres
          </label>
          <input id="email" type="email" autoComplete="email" value={draft.customer.email} onChange={e => set({
          email: e.target.value
        })} aria-invalid={Boolean(errors.email)} className={field(Boolean(errors.email))} placeholder="naam@voorbeeld.nl" data-id="element-427" data-mp="1vw5yifu23mq" />
          {errors.email && <p className="mt-1.5 text-xs text-red-400" data-id="element-428" data-mp="gk0tbzptz3bu">{errors.email}</p>}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-429" data-mp="3f3z9o5jwa12d">
          <div data-id="element-430" data-mp="3k0poyqnvksmq">
            <label htmlFor="car" className="text-sm font-medium text-chalk" data-id="element-431" data-mp="3k2aj4f6a29k2">
              Merk en model
            </label>
            <input id="car" type="text" value={draft.customer.car} onChange={e => set({
            car: e.target.value
          })} aria-invalid={Boolean(errors.car)} className={field(Boolean(errors.car))} placeholder="Bijv. Audi A4 Avant" data-id="element-432" data-mp="3p2orjybsjlim" />
            {errors.car && <p className="mt-1.5 text-xs text-red-400" data-id="element-433" data-mp="16u152ajwps5s">{errors.car}</p>}
          </div>
          <div data-id="element-434" data-mp="12thbti2gf8ox">
            <label htmlFor="plate" className="text-sm font-medium text-chalk" data-id="element-435" data-mp="12tzg91z9st7x">
              Kenteken <span className="text-dim" data-id="element-436" data-mp="1gtibiu6zga2t">(optioneel)</span>
            </label>
            <input id="plate" type="text" value={draft.customer.plate} onChange={e => set({
            plate: e.target.value.toUpperCase()
          })} className={field(false)} placeholder="XX-000-X" data-id="element-437" data-mp="1btvxwjn8srth" />
          </div>
        </div>

        <div className="mt-5" data-id="element-438" data-mp="1zj6zu4gbws97">
          <label htmlFor="notes" className="text-sm font-medium text-chalk" data-id="element-439" data-mp="1zif7d2i13an5">
            Opmerkingen <span className="text-dim" data-id="element-440" data-mp="1uk3x4qntmy4e">(optioneel)</span>
          </label>
          <textarea id="notes" rows={4} value={draft.customer.notes} onChange={e => set({
          notes: e.target.value
        })} className={`${field(false)} resize-none`} placeholder="Staat van de lak, wensen, aandachtspunten…" data-id="element-441" data-mp="29hee2fj8gw7i" />
        </div>
      </div>

      <div data-id="element-442" data-mp="2s4x0p8j3sk5q">
        <h3 className="text-xs uppercase tracking-[0.2em] text-dim" data-id="element-443" data-mp="2s4nbhn5vuh13">Betaling</h3>
        <div className="mt-4 space-y-3" data-id="element-444" data-mp="2e6yn45clwrra">
          {methods.map(method => {
          const Icon = method.icon;
          const selected = draft.paymentMethod === method.id;
          return <button key={method.id} type="button" aria-pressed={selected} onClick={() => onChange({
            paymentMethod: method.id
          })} className={`flex w-full gap-4 rounded-2xl border p-5 text-left transition-colors duration-200 ease-smooth ${selected ? 'border-ember bg-panel' : 'border-edge bg-carbon hover:border-ember/40'}`} data-id="element-445" data-mp="24fb2anak28ys">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-ember bg-ember text-void' : 'border-edge text-dim'}`} data-id="element-446" data-mp="1qgl5l8rnebay">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-447" data-mp="12jojl5pz0g20" />
                </span>
                <span data-id="element-448" data-mp="xn7t44vatplr">
                  <span className="block font-display text-base font-extrabold tracking-tight text-chalk" data-id="element-449" data-mp="xmg0n2x007zp">
                    {method.title}
                  </span>
                  <span className="mt-1.5 block text-sm leading-relaxed text-dim" data-id="element-450" data-mp="1q9smfavc4bry">{method.text}</span>
                  <span className="mt-3 inline-flex rounded-full border border-ember/40 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-ember" data-id="element-451" data-mp="1lcshx4e4vh2y">
                    {method.badge}
                  </span>
                </span>
              </button>;
        })}
        </div>

        <p className="mt-5 text-xs leading-relaxed text-dim" data-id="element-452" data-mp="4xr47h6w0qua">
          Gratis annuleren of verzetten tot 24 uur voor uw afspraak. Een aanbetaling wordt dan volledig
          terugbetaald.
        </p>
      </div>
    </div>;
}